import styled from "@emotion/styled/macro";
import {
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import { Container } from "components/atoms/GlobalStyles";
import chevronDown from "components/assets/images/chevron-down.svg";
import { OPTY_PROGRESS_STEPS } from "checkout/util/constants";

type props = {
  showSolarStepsDetails: boolean;
  setShowSolarStepsDetails: (arg: boolean) => void;
  showDoubleColumnStyles?: boolean;
  showSingleColumnStyles?: boolean;
};

type AgendaStepProps = {
  stepNumber: string;
  description: string;
  img: string;
  showSingleColumnStyles?: boolean;
  showDoubleColumnStyles?: boolean;
  color?: string;
  completed?: boolean;
  current?: boolean;
};

const AgendaStep = ({
  stepNumber,
  description,
  img,
  showSingleColumnStyles = false,
  showDoubleColumnStyles = false,
  color,
  completed,
  current,
}: AgendaStepProps) => {
  return (
    <AgendaStepContainer
      showSingleColumnStyles={showSingleColumnStyles}
      showDoubleColumnStyles={showDoubleColumnStyles}
    >
      <img src={img} alt={description} />

      <StyledTextContainer showDoubleColumnStyles={showDoubleColumnStyles}>
        <StyledStepNumber color={color}>
          <Typography size={12}>{stepNumber}</Typography>
          {completed && (
            <StepMarker>
              <SvgIcon
                style={{ paddingTop: 2 }}
                width={16}
                height={16}
                color={tokens.BRAND_MEDIUM_BLUE}
                name={SvgNames.CheckEnclosed}
              />
            </StepMarker>
          )}
          {current && (
            <StepMarker>
              <SvgIcon
                width={16}
                height={16}
                color={tokens.BRAND_MEDIUM_BLUE}
                name={SvgNames.ArrowLeftFilled}
              />
            </StepMarker>
          )}
        </StyledStepNumber>
        <StyledDescription color={color}>{description}</StyledDescription>
      </StyledTextContainer>
    </AgendaStepContainer>
  );
};

const SolarSteps = ({
  showSolarStepsDetails,
  setShowSolarStepsDetails,
  showDoubleColumnStyles = false,
  showSingleColumnStyles = false,
}: props) => {
  return (
    <SolarStepsContainer>
      <SolarStepsHeader
        showSolarStepsDetails={showSolarStepsDetails}
        onClick={() => setShowSolarStepsDetails(!showSolarStepsDetails)}
      >
        <SolarStepsTitle>
          We are here as your expert guide every step of the way.
        </SolarStepsTitle>
        <SolarStepArrow
          showSolarStepsDetails={showSolarStepsDetails}
          src={chevronDown}
          alt="Chevron arrow"
        />
      </SolarStepsHeader>
      <SolarStepsContent
        showSolarStepsDetails={showSolarStepsDetails}
        showDoubleColumnStyles={showDoubleColumnStyles}
        showSingleColumnStyles={showSingleColumnStyles}
      >
        {OPTY_PROGRESS_STEPS.map((solarStep) => {
          return (
            <AgendaStep
              key={solarStep?.stepTitle}
              showSingleColumnStyles={showSingleColumnStyles}
              showDoubleColumnStyles={showDoubleColumnStyles}
              img={solarStep?.img}
              stepNumber={solarStep?.stepTitle}
              description={solarStep?.description}
            />
          );
        })}
      </SolarStepsContent>
    </SolarStepsContainer>
  );
};

const SolarStepsContainer = styled(Container)``;

const SolarStepsHeader = styled.div<{
  showSolarStepsDetails: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${({ showSolarStepsDetails }) =>
    showSolarStepsDetails ? "0px" : "32px"};
`;

const SolarStepsTitle = styled.div`
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
`;

const SolarStepArrow = styled.img<{
  showSolarStepsDetails: boolean;
}>`
  transition: 0.5s;
  width: 24px;
  height: 24px;
  transform: ${({ showSolarStepsDetails }) =>
    showSolarStepsDetails ? "rotate(180deg)" : ""};
`;

const SolarStepsContent = styled.div<{
  showSolarStepsDetails: boolean;
  showDoubleColumnStyles: boolean;
  showSingleColumnStyles: boolean;
}>`
  justify-content: space-between;
  transition: all 0.5s;
  align-items: baseline;
  padding-top: ${({ showDoubleColumnStyles }) =>
    showDoubleColumnStyles ? "24px" : "40px"};
  display: ${({ showSolarStepsDetails }) =>
    showSolarStepsDetails ? "flex" : "none"};
  flex-wrap: ${({ showDoubleColumnStyles, showSingleColumnStyles }) =>
    showDoubleColumnStyles && !showSingleColumnStyles ? "wrap" : "nowrap"};
  flex-direction: ${({ showDoubleColumnStyles }) =>
    showDoubleColumnStyles ? "column" : "unset"};
  height: ${({ showSingleColumnStyles, showDoubleColumnStyles }) =>
    showSingleColumnStyles
      ? "660px"
      : showDoubleColumnStyles
      ? "360px"
      : "unset"};
`;

const StyledTextContainer = styled.div<{
  showDoubleColumnStyles: boolean | undefined;
}>`
  padding-left: ${({ showDoubleColumnStyles }) =>
    showDoubleColumnStyles ? "16px" : "0px"};
`;

const StyledStepNumber = styled.div<{
  color?: string;
}>`
  display: flex;
  padding-top: 12px;
  font-weight: 500;
  color: ${({ color }) => (color ? color : tokens.BRAND_HERO_BLUE)};
`;

const StepMarker = styled.span`
  margin-left: 5px;
`;

const StyledDescription = styled.div<{
  color?: string;
}>`
  font-size: 14px;
  line-height: 24px;
  font-weight: 500;
  color: ${({ color }) => (color ? color : tokens.BRAND_HERO_BLUE)};
`;

const AgendaStepContainer = styled.div<{
  showSingleColumnStyles: boolean;
  showDoubleColumnStyles: boolean | undefined;
}>`
  padding-bottom: ${({ showDoubleColumnStyles }) =>
    showDoubleColumnStyles ? "24px" : "32px"};
  padding-right: ${({ showDoubleColumnStyles }) =>
    showDoubleColumnStyles ? "0px" : "16px"};
  display: flex;
  align-items: flex-start;
  flex-direction: ${({ showDoubleColumnStyles }) =>
    showDoubleColumnStyles ? "row" : "column"};
  min-width: ${({ showSingleColumnStyles }) =>
    showSingleColumnStyles ? "11%" : "9%"};
`;

export { SolarSteps, SolarStepsContent, AgendaStep };
